import React, {
    ChangeEvent,
    forwardRef,
    ReactElement,
    ReactNode,
    Ref,
    FocusEvent,
    FC,
    CSSProperties
} from 'react';
import theme from '@vromo/ui/components/theme';
import Tooltip from '@vromo/ui/components/Tooltip';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { css } from 'emotion';
import snakeCase from 'lodash/snakeCase';
import { useTranslation } from 'react-i18next';
import NoLocation from '@vromo/icons/NoLocation';
import styles from './input.style';

export const inputClassName = (error?: string, lockSize?: boolean): string => {
    const borderStyle = `1px solid ${error ? 'red' : theme.lightGrey}`;
    return css`
        ${lockSize ? 'resize: none;' : ''}
        padding: 6px 12px;
        border: ${borderStyle};
        border-radius: 4px;
        transition: border-color 0.15s ease-in-out 0s, box-shadow 0.15s ease-in-out 0s;
        background-color: white;
        display: block;
        outline: none;
        width: 100%;
        height: 34px;

        ::placeholder {
            color: ${theme.grey};
            transition: all 0ms.15s ease-in-out 0s;
        }

        &:hover:enabled {
            border-color: ${error ? 'red' : '#bebec3'};
        }

        &:focus {
            border-color: ${error ? 'red' : '#2c9ee0 !important'};
            box-shadow: none;
            outline: 0;

            ::placeholder {
                color: #e5e5e6;
            }
        }
    `;
};

interface LabelContentProps {
    children?: ReactNode;
    required?: boolean;
    label?: string;
    optional?: boolean;
    tooltip?: string;
    smalllabel?: string;
}

function LabelContent(props: LabelContentProps): ReactElement {
    const { children, required = false, label, optional = false, tooltip, smalllabel } = props;
    const { t } = useTranslation();
    return (
        <>
            {required ? <small>* </small> : null}
            {label}
            {optional ? (
                <small style={{ color: `${theme.grey}`, fontStyle: 'italic', marginLeft: 5 }}>
                    {`- ${t('common.optional')}`}
                </small>
            ) : null}
            {smalllabel ? (
                <small style={{ color: `${theme.grey}`, fontStyle: 'italic', marginLeft: 5 }}>
                    {smalllabel}
                </small>
            ) : null}
            {tooltip?.length ? (
                <Tooltip position="bottom" label={tooltip}>
                    <div style={{ display: 'inline-block', marginLeft: 10 }}>
                        <FontAwesome icon="info-circle" weight="duotone" colour={theme.lightGrey} />
                    </div>
                </Tooltip>
            ) : null}
            <div style={{ fontWeight: 'normal' }}>{children}</div>
        </>
    );
}

export function InputLabel(
    props: LabelContentProps & {
        ['data-testid']?: string;
        noLabelComponent?: boolean;
    }
): ReactElement {
    const { noLabelComponent } = props;
    const testIdObject = { ...(props['data-testid'] && { ['data-testid']: props['data-testid'] }) };
    if (noLabelComponent)
        return (
            <div style={styles.inputLabel} {...testIdObject}>
                <LabelContent {...props} />
            </div>
        );
    return (
        <label style={styles.inputLabel} {...testIdObject}>
            <LabelContent {...props} />
        </label>
    );
}

type InputType = 'text' | 'number' | 'email' | 'password';

interface ContitionalWrapperProps {
    condition?: boolean;
    wrapper: any;
    children?: ReactNode;
}

const ConditionalWrapper: FC<ContitionalWrapperProps> = ({ condition, wrapper, children }) =>
    condition ? wrapper(children) : children;

export default forwardRef(function Input(
    props: InInputIconProps & {
        disabled?: boolean;
        field?: string;
        id?: string;
        label?: string;
        maxLength?: number;
        max?: string | number;
        min?: string | number;
        name?: string;
        onBlur?: JSX.IntrinsicElements['input']['onBlur'];
        onChange?: JSX.IntrinsicElements['input']['onChange'];
        onBeforeInput?: JSX.IntrinsicElements['input']['onBeforeInput'];
        onKeyDown?: JSX.IntrinsicElements['input']['onKeyDown'];
        optional?: boolean;
        placeholder?: string;
        required?: boolean;
        smalllabel?: string;
        step?: string | number;
        style?: { [key: string]: string | number };
        tooltip?: string;
        type?: InputType;
        value?: string | number;
        noTopMargin?: boolean;
        ['data-testid']?: string;
    },
    ref: Ref<HTMLInputElement>
) {
    const {
        required,
        label,
        optional,
        tooltip,
        smalllabel,
        onClearClick,
        errorMessage,
        noTopMargin,
        ...inputProps
    } = props;
    return (
        <ConditionalWrapper
            wrapper={(children?: ReactNode) => (
                <InputLabel
                    required={required}
                    label={label}
                    optional={optional}
                    tooltip={tooltip}
                    smalllabel={smalllabel}>
                    {children}
                </InputLabel>
            )}
            condition={!!label}>
            <InputGroup noTopMargin={noTopMargin}>
                <input
                    {...inputProps}
                    required={required}
                    name={snakeCase(label)}
                    className={inputClassName(errorMessage)}
                    ref={ref}
                    onBeforeInput={props.onBeforeInput}
                    onKeyDown={props.onKeyDown}
                    {...(props['data-testid'] && { ['data-testid']: props['data-testid'] })}
                />
                <InInputIcon onClearClick={onClearClick} errorMessage={errorMessage} />
            </InputGroup>
        </ConditionalWrapper>
    );
});

interface InInputIconProps {
    onClearClick?: () => void;
    errorMessage?: string;
}

const InInputIcon: FC<InInputIconProps> = ({ onClearClick, errorMessage }) => {
    if (onClearClick)
        return (
            <div
                style={{
                    top: 9,
                    right: 11,
                    position: 'absolute',
                    cursor: 'pointer'
                }}>
                <FontAwesome icon="times" onClick={onClearClick} />
            </div>
        );
    if (errorMessage)
        return (
            <div
                style={{
                    top: 9,
                    right: 11,
                    position: 'absolute',
                    cursor: 'pointer'
                }}>
                <Tooltip position="bottom" label={errorMessage}>
                    <div style={{ display: 'inline-block', marginLeft: 10 }}>
                        <FontAwesome icon="info-circle" weight="duotone" color="red" />
                    </div>
                </Tooltip>
            </div>
        );
    return null;
};

export function InputGroup({
    children,
    noTopMargin
}: {
    children: ReactNode;
    noTopMargin?: boolean;
}): ReactElement {
    return <div style={{ position: 'relative', marginTop: !noTopMargin ? 5 : 0 }}>{children}</div>;
}

export function InputGroupAddon({ children }: { children: ReactNode }): ReactElement {
    return (
        <span style={{ position: 'absolute', top: 0, right: 0, bottom: 0, padding: 8 }}>
            {children}
        </span>
    );
}

export function TextArea(props: {
    disabled?: boolean;
    errorMessage?: string;
    label?: string;
    maxLength?: number;
    onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    onFocus?: (e: FocusEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
    optional?: boolean;
    style?: CSSProperties;
    value?: string;
    lockSize?: boolean;
}): ReactElement {
    const { label, optional, errorMessage, lockSize, ...textAreaProps } = props;
    const { value, maxLength } = textAreaProps;
    return label?.length ? (
        <InputLabel label={label} optional={optional}>
            <textarea {...textAreaProps} className={inputClassName(errorMessage, lockSize)} />
            {maxLength ? (
                <small style={{ color: `${theme.grey}`, fontStyle: 'italic' }}>{`${
                    value?.length ? value.length : 0
                }/${maxLength}`}</small>
            ) : null}
        </InputLabel>
    ) : (
        <textarea {...textAreaProps} className={inputClassName(errorMessage, lockSize)} />
    );
}
